import { UseQueryResult } from '@tanstack/react-query';
import React from 'react';
import { Spinner } from '../../components';
import { ViewError, ViewLoader } from './components';

interface QueryErrorWithAxiosStatus {
  axiosError?: {
    response?: {
      status?: number;
    };
  };
}

interface Props<TData = unknown, TError = unknown> {
  query: Pick<UseQueryResult<TData, TError>, 'isLoading' | 'error' | 'data'>;
  children: ((data: TData) => React.ReactElement) | React.ReactNode;
  loader?: React.ReactNode;
  inline?: boolean;
  ignore404?: boolean;

  noLoader?: boolean;
}

export function QueryView<TData = unknown, TError = unknown>({
  query,
  children,
  loader,
  inline,
  ignore404,
  noLoader,
}: Props<TData, TError>) {
  if (query.isLoading) {
    if (noLoader) {
      return null;
    }

    return <>{loader ?? (inline ? <Spinner /> : <ViewLoader />)}</>;
  }

  if (
    query.error &&
    !(ignore404 && (query.error as QueryErrorWithAxiosStatus)?.axiosError?.response?.status === 404)
  ) {
    return <ViewError error={query.error as unknown as Error} />;
  }

  if (typeof query.data !== 'undefined' || ignore404) {
    if (typeof children === 'function') {
      return children(query.data as TData);
    }

    return <>{children}</>;
  }

  return null;
}
